import type { Delivery } from '../interfaces/ILogistics.js';
import type { DeliveryOrderLineBody } from '../apis/logistics.js';
import type { OrderPrefill } from './orderPrefill.js';
import { fold, parseNumber } from '../utils/text.js';
import { sameUnit, unitToStore, type StockUnitOf } from '../../logistics/index.js';

export type { StockUnitOf };
import { quantityIssue } from './quantity.js';

/**
 * The order form's lines table, as pure state.
 *
 * One draft per row. Amounts stay STRINGS the whole way through, exactly as
 * the single-material form kept its quantity: an empty field means "not
 * recorded", which is a different claim from 0 — and 0 would assert an empty
 * truck.
 */
export interface IDeliveryLineDraft {
  /** The stored line's id, so an edit is a CHANGE in the order's history
   *  rather than a remove-plus-add. Empty for a row the user just added. */
  id: string;
  /** React's key. Never the material name (renaming a row would remount it
   *  mid-keystroke) and never the index (removing a row above would hand the
   *  next one someone else's state). */
  key: string;
  itemName: string;
  quantity: string;
  unit: string;
  /** The unit is a fact of the picked material, not a free choice — see the
   *  form's own `unitLocked` reasoning, now kept per row. */
  unitLocked: boolean;
  /** The stored line's material is already synced into stock: the line is
   *  history, shown read-only and not removable. False for any new row. */
  synced: boolean;
  /** What picking this material filled the row from (or, on the first row of
   *  an "Order now", the seed the inventory panel handed in) — null for a row
   *  typed by hand, which the system inferred nothing about.
   *
   *  Kept on the DRAFT rather than in a parallel map because every suggestion
   *  the order makes is re-derived from the SET of them: a row and the facts
   *  behind it have to be added and removed as one thing, or the order-level
   *  suggestions outlive the material they came from. */
  prefill: Partial<OrderPrefill> | null;
  /** The unit a stored line was saved in, when an edit switched it to the
   *  material's stock unit — the form asks for the amount to be re-checked
   *  (it is never converted). Absent on every other row. */
  unitWas?: string;
}


let nextKey = 0;

/** A fresh, empty row. */
export function blankDraft(): IDeliveryLineDraft {
  nextKey += 1;
  return {
    id: '',
    key: `new-${nextKey}`,
    itemName: '',
    quantity: '',
    unit: '',
    unitLocked: false,
    synced: false,
    prefill: null,
  };
}

/**
 * What the form opens with.
 *
 * An EDIT shows the order's stored lines, ids kept. A CREATE opens with one
 * row per prefill ("Order now" hands one, a Stock tab checkout one per
 * material it orders) and a single blank row with none — the user adds the
 * rest.
 *
 * `unitEditable` is the hand-off's escape hatch: a claim's unit is a starting
 * guess, not a fact of a known material, so it must not open locked.
 *
 * A material the site counts in stock overrides both: its lines use the
 * stock's unit, locked (QA 2026-10-18 — a delivery edited to kg was summed into
 * a count in bags). An edited line saved in another unit is switched and keeps
 * `unitWas`; a synced line is history and stays as stored.
 */
export function draftsFromInitial(
  initial: Delivery | null,
  prefills: Partial<OrderPrefill>[],
  unitEditable: boolean,
  stockUnitOf: StockUnitOf,
): IDeliveryLineDraft[] {
  if (initial) {
    return initial.lines.map((line) => {
      const stored = line.unit ?? '';
      const stockUnit = line.isSynced ? null : stockUnitOf(line.itemName);
      return {
        id: line.id,
        key: line.id,
        itemName: line.itemName,
        quantity: line.quantity != null ? String(line.quantity) : '',
        unit: stockUnit ?? stored,
        // An untracked material's unit stays the user's to change.
        unitLocked: stockUnit != null,
        ...(stockUnit != null && stored !== '' && !sameUnit(stored, stockUnit) ? { unitWas: stored } : {}),
        synced: line.isSynced,
        // A stored line is the user's own saved answer, never a suggestion.
        prefill: null,
      };
    });
  }
  if (prefills.length === 0) return [blankDraft()];
  return prefills.map((prefill) => {
    const stockUnit = stockUnitOf(prefill.itemName ?? '');
    const unit = stockUnit ?? prefill.unit ?? '';
    return {
      ...blankDraft(),
      itemName: prefill.itemName ?? '',
      quantity: prefill.quantity != null ? String(prefill.quantity) : '',
      unit,
      unitLocked: stockUnit != null || (!unitEditable && unit !== ''),
      // "Order now" is the one-material case of a pick: the row carries the
      // seed it was filled from, exactly as a picked row does.
      prefill,
    };
  });
}

/** A typed amount must be a plain positive decimal within the server's cap —
 *  blocked here rather than letting the server 400. Blank stays legal: an
 *  unsent draft may not know the amount yet. `quantityIssue` says why not. */
export function quantityValid(raw: string): boolean {
  return quantityIssue(raw) === null;
}

/** The numeric amount, or null for "not recorded". */
export function quantityOf(raw: string): number | null {
  return raw.trim() === '' ? null : parseNumber(raw);
}

/**
 * The row already holding a fold-equal material, or -1.
 *
 * A material appears once per order, so picking one that is already on it
 * focuses that row instead of adding a second — the rule the dialog enforces
 * live and the server enforces with a 400.
 */
export function duplicateIndexOf(
  drafts: IDeliveryLineDraft[],
  name: string,
  exceptIndex: number,
): number {
  const key = fold(name.trim());
  if (key === '') return -1;
  return drafts.findIndex((d, i) => i !== exceptIndex && fold(d.itemName.trim()) === key);
}

/**
 * Every row usable, as the server would judge it: a material name, a valid
 * amount, no duplicate material — and, once the request has gone out, an
 * amount AND its unit on every line (a quantity without its unit is not an
 * amount).
 */
export function linesValid(drafts: IDeliveryLineDraft[]): boolean {
  return drafts.every((draft, i) => {
    if (draft.itemName.trim() === '') return false;
    if (duplicateIndexOf(drafts, draft.itemName, i) !== -1) return false;
    if (!quantityValid(draft.quantity)) return false;
    // A line switched to its stock unit holds an amount typed in another one
    // (20 kg is not 20 bags): it saves once the amount is re-entered.
    if (draft.unitWas != null) return false;
    return draft.quantity.trim() !== '' && draft.unit !== '';
  });
}

/** The rows as the write body carries them — ids only where one exists, so a
 *  new row reads as new. */
export function draftsToBody(drafts: IDeliveryLineDraft[]): DeliveryOrderLineBody[] {
  return drafts.map((draft) => ({
    ...(draft.id === '' ? {} : { id: draft.id }),
    itemName: draft.itemName.trim(),
    quantity: parseNumber(draft.quantity),
    // The unit's KEY when the vocabulary knows it, whichever language the
    // picker rendered it in — see shared/logistics/units.
    unit: unitToStore(draft.unit),
  }));
}
